import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { RequireAuth } from 'components/Auth/RequireAuth';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useDiscord, DiscordProvider, Discord } from './StaffDiscordContext';
import StaffDiscordsTable from './StaffDiscordsTable';
import NavPills from 'components/NavPills/NavPills';
import GridContainer from 'components/Grid/GridContainer';
import { Theme } from '@mui/material/styles';
import styles from '../../../assets/jss/material-kit-react/views/profilePage';
import { makeStyles } from '@mui/styles';
import StaffDiscordsForm from './StaffDiscordForm';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
  },
  gridContainer: {
    height: '100%',
    width: '100%',
  },
  dataGridContainer: {
    display: 'flex',
    height: '100%',
    '& .MuiDataGrid-root': {
      flexGrow: 1,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      '& .MuiDataGrid-cell': {
        borderColor: theme.palette.divider,
      },
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
      '& .MuiDataGrid-footerContainer': {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
      },
    },
  },
}));

function StaffDiscordsContent() {
  const sessionToken = localStorage.getItem('session_token');
  const [loadingData, setLoadingData] = useState(true);
  const [selectedDiscord, setSelectedDiscord] = useState<Discord | null>(null);
  const [selectionCount, setSelectionCount] = useState(0);
  const { t } = useTranslation();
  useDocumentTitle(t('navigation.staff'));
  const classes = useStyles();

  const { discord, isLoading, deleteDiscord } = useDiscord();

  if (!sessionToken) {
    return <Navigate to="/" replace />;
  }

  if (loadingData) {
    setLoadingData(false);
  }

  const handleEdit = (discord: Discord) => {
    setSelectedDiscord(discord);
    setSelectionCount(1);
  };

  const handleDelete = (discord: Discord) => {
    deleteDiscord(discord.id);
  };

  const handleDeleteSelected = (ids: string[]) => {
    ids.forEach((id) => deleteDiscord(id));
  };

  const handleFormReset = () => {
    setSelectedDiscord(null);
    setSelectionCount(0);
  };

  return (
    <RequireAuth requiredPermission="is_staff">
      <div>
        <div className={classes.mainContainer}>
          <div className={classes.container}>
            <div className={classes.contentContainer}>
              <NavPills
                queryKey={'t'}
                alignCenter
                fullWidth
                onChange={function () {}}
                color="primary"
                tabs={[
                  {
                    tabName: 'Discords',
                    tabButton: `Discords Overview`,
                    tabContent: (
                      <GridContainer justify="center">
                        {isLoading ? (
                          'loading'
                        ) : (
                          <StaffDiscordsTable
                            discords={discord || []}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onDeleteSelected={handleDeleteSelected}
                          />
                        )}
                        <StaffDiscordsForm
                          selectedDiscord={selectedDiscord}
                          onUpdate={handleFormReset}
                          selectionCount={selectionCount}
                          onClose={handleFormReset}
                        />
                      </GridContainer>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>
    </RequireAuth>
  );
}

export default function StaffDiscordsPage() {
  return (
    <DiscordProvider>
      <StaffDiscordsContent />
    </DiscordProvider>
  );
}
